/** Generic batch-edit client: the catalog endpoint + the wire shapes of the
 *  `batch_edit` action on each grid's `/batch/` endpoint.
 *
 *  The catalog (`GET /batch_edit/catalog/`) is the single source of truth for
 *  the dialog: which resources are editable, which paths of their read
 *  documents may be mutated with which operators, enum choices with labels,
 *  and the conditional-operator vocabulary. A path added server-side appears
 *  here with no frontend rebuild. The OpenAPI spec mirrors the same registry
 *  (`x-batch-edit-paths` on BatchEditParams) and a backend test asserts
 *  equality; `batchEdit.contract.test.ts` asserts this module's hardcoded
 *  vocabularies are subsets of the spec.
 *
 *  Hand-authored per console convention (feature modules never depend on the
 *  generated types).
 */
import { apiGet } from './client';

// ---------------------------------------------------------------------------
// Catalog
// ---------------------------------------------------------------------------

export type BatchEditPathType = 'dotty' | 'jsonpath';

export type BatchEditEntryType =
  | 'string'
  | 'integer'
  | 'number'
  | 'boolean'
  | 'enum'
  | 'datetime'
  | 'date'
  | 'time';

export interface BatchEditChoice {
  value: unknown;
  label: string;
}

export interface BatchEditPath {
  path: string;
  type: BatchEditEntryType;
  label: string;
  help: string;
  example?: unknown;
  nullable: boolean;
  ops: string[];
  choices?: BatchEditChoice[];
  min_value?: number;
}

export interface BatchEditResource {
  label: string;
  view_perm: string;
  max_selection: number;
  /** `updated_ts` or `document` — how per-row staleness is fingerprinted. */
  fingerprint: string;
  paths: BatchEditPath[];
}

export interface BatchEditCatalog {
  path_types: BatchEditPathType[];
  condition_operators: string[];
  coerce_kinds: string[];
  resources: Record<string, BatchEditResource>;
}

export async function fetchBatchEditCatalog(): Promise<BatchEditCatalog> {
  return apiGet<BatchEditCatalog>('/batch_edit/catalog/');
}

// ---------------------------------------------------------------------------
// Wire shapes (params of the `batch_edit` action)
// ---------------------------------------------------------------------------

export interface BatchEditConditionArg {
  type: 'constant' | BatchEditPathType;
  value: unknown;
}

export interface BatchEditCondition {
  left: BatchEditConditionArg;
  op: string;
  right?: BatchEditConditionArg;
  coerce?: 'number' | 'string' | 'boolean' | 'datetime';
  not?: boolean;
}

export interface BatchEditConditionGroup {
  bool: 'AND' | 'OR';
  conditions: Array<BatchEditCondition | BatchEditConditionGroup>;
  not?: boolean;
}

export interface BatchEditOp {
  path: string;
  op: string;
  value?: unknown;
}

export interface BatchEditBlock {
  conditions?: Array<BatchEditCondition | BatchEditConditionGroup>;
  ops: BatchEditOp[];
}

export interface BatchEditParams extends Record<string, unknown> {
  reason: string;
  dry_run: boolean;
  receipt?: string;
  path_type: BatchEditPathType;
  blocks: BatchEditBlock[];
}

/** Comparison operators that take no right-hand argument. */
export const PRESENCE_OPERATORS = ['exists', 'is_null'] as const;

export const CONDITION_OPERATOR_LABELS: Record<string, string> = {
  eq: '=',
  neq: '≠',
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
  str_eq: 'equals (case-sensitive)',
  str_ieq: 'equals (ignore case)',
  str_contains: 'contains (case-sensitive)',
  str_icontains: 'contains (ignore case)',
  str_regex: 'matches regex',
  str_iregex: 'matches regex (ignore case)',
  exists: 'exists',
  is_null: 'is null',
  in: 'is one of',
};

export const MUTATION_OP_LABELS: Record<string, string> = {
  set: 'Set to',
  append: 'Append',
  prepend: 'Prepend',
  regex_replace: 'Regex replace',
  inc: 'Increase by',
  dec: 'Decrease by',
  flip: 'Flip',
  add: 'Add duration',
  subtract: 'Subtract duration',
  unset: 'Clear (set null)',
  set_if_null: 'Set only if null',
};

// ---------------------------------------------------------------------------
// Task result (dry-run report / apply report)
// ---------------------------------------------------------------------------

export type BatchEditOutcomeCode =
  | 'applied'
  | 'would_change'
  | 'no_change'
  | 'conditions_not_met'
  | 'condition_error'
  | 'stale'
  | 'missing'
  | 'rejected_path'
  | 'rejected_value'
  | 'rejected_regex_timeout'
  | 'failed';

export const OUTCOME_LABELS: Record<BatchEditOutcomeCode, string> = {
  applied: 'Changed',
  would_change: 'Will change',
  no_change: 'No change',
  conditions_not_met: 'Conditions not met',
  condition_error: 'Condition error',
  stale: 'Changed since dry run',
  missing: 'No longer exists',
  rejected_path: 'Path not editable',
  rejected_value: 'Value rejected',
  rejected_regex_timeout: 'Pattern too slow',
  failed: 'Failed',
};

export const OUTCOME_SEVERITY: Record<BatchEditOutcomeCode, 'success' | 'info' | 'warning' | 'error'> = {
  applied: 'success',
  would_change: 'success',
  no_change: 'info',
  conditions_not_met: 'info',
  condition_error: 'error',
  stale: 'warning',
  missing: 'warning',
  rejected_path: 'error',
  rejected_value: 'error',
  rejected_regex_timeout: 'error',
  failed: 'error',
};

export interface BatchEditConditionTrace {
  kind: 'cond' | 'group';
  result: boolean;
  negated: boolean;
  // cond
  left?: BatchEditConditionArg;
  left_value?: unknown;
  op?: string;
  right?: BatchEditConditionArg;
  right_value?: unknown;
  coerced_to?: string | null;
  note?: string | null;
  // group
  bool?: 'AND' | 'OR';
  children?: BatchEditConditionTrace[];
}

export interface BatchEditChange {
  path: string;
  from: unknown;
  op: string;
  to: unknown;
  note?: string;
}

export interface BatchEditBlockReport {
  matched: boolean;
  conditions: BatchEditConditionTrace[];
  changes: BatchEditChange[];
}

export interface BatchEditResultEntry {
  id: number | string;
  repr?: string | null;
  tenant_id?: number | null;
  outcome: BatchEditOutcomeCode;
  error?: string;
  blocks?: BatchEditBlockReport[];
}

export interface BatchEditTaskResult {
  dry_run: boolean;
  resource: string;
  total: number;
  counts: Partial<Record<BatchEditOutcomeCode, number>>;
  results: BatchEditResultEntry[];
  results_truncated: boolean;
  /** Present on dry-run results — echo it back as `params.receipt` to apply. */
  receipt?: string;
}
